import { CalendarDaysIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useMaskito } from "@maskito/react";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import * as React from "react";
import { DateRange } from "react-day-picker";
import { Button } from "@/core/components/ui/button";
import { Calendar } from "@/core/components/ui/calendar/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { Stack } from "@/core/components/ui/stack";
import {
  createTypeableDateMask,
  parseTypeableDate,
} from "@/core/components/ui/typeable-date-picker/typeable-date";
import {
  dayjs,
  FORMAT_TYPEABLE_DATE,
  FORMAT_TYPEABLE_DATE_PLACEHOLDER,
} from "@/core/utils/date";
import { cn } from "@/core/utils/utils";
import {
  anchorMonth,
  createDateLabel,
  MONTHS_ON_SCREEN,
  RANGE_END,
  type RangeEnd,
} from "./utils";

// Add quarter support to dayjs
dayjs.extend(quarterOfYear);

export interface TimeRange {
  from: Date;
  to: Date;
  label: string;
}

export interface TimeRangePreset {
  label: string;
  getValue: () => TimeRange;
}

const DEFAULT_PRESETS: TimeRangePreset[] = [
  {
    label: "This week",
    getValue: () => ({
      from: dayjs().startOf("week").toDate(),
      to: dayjs().endOf("week").toDate(),
      label: "This week",
    }),
  },
  {
    label: "This month",
    getValue: () => ({
      from: dayjs().startOf("month").toDate(),
      to: dayjs().endOf("month").toDate(),
      label: "This month",
    }),
  },
  {
    label: "Last month",
    getValue: () => ({
      from: dayjs().subtract(1, "month").startOf("month").toDate(),
      to: dayjs().subtract(1, "month").endOf("month").toDate(),
      label: "Last month",
    }),
  },
  {
    label: "Last 60 days",
    getValue: () => ({
      from: dayjs().subtract(60, "days").toDate(),
      to: dayjs().toDate(),
      label: "Last 60 days",
    }),
  },
  {
    label: "This quarter",
    getValue: () => ({
      from: dayjs().startOf("quarter").toDate(),
      to: dayjs().endOf("quarter").toDate(),
      label: "This quarter",
    }),
  },
  {
    label: "Last quarter",
    getValue: () => ({
      from: dayjs().subtract(1, "quarter").startOf("quarter").toDate(),
      to: dayjs().subtract(1, "quarter").endOf("quarter").toDate(),
      label: "Last quarter",
    }),
  },
  {
    label: "This year",
    getValue: () => ({
      from: dayjs().startOf("year").toDate(),
      to: dayjs().endOf("year").toDate(),
      label: "This year",
    }),
  },
];

interface TimeRangePickerProps {
  value?: TimeRange | null;
  onChange?: (range: TimeRange | null) => void;
  onSelect?: (range: TimeRange | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  fullWidth?: boolean;
  presets?: TimeRangePreset[];
  /**
   * Adds From and To fields that a date can be typed into, for ranges the
   * presets do not cover. Off by default: on a filter over recent data the
   * presets are the whole story, and clicking two days is faster than typing
   * them. Turn it on where any range is a fair question, such as a report that
   * can be run for an arbitrary period.
   */
  allowTextEntry?: boolean;
}

const TimeRangePicker = React.forwardRef<
  HTMLButtonElement,
  TimeRangePickerProps
>(
  (
    {
      value,
      onChange,
      onSelect,
      label,
      placeholder = "Select time range",
      required = false,
      fullWidth = false,
      presets = DEFAULT_PRESETS,
      allowTextEntry = false,
    },
    ref,
  ) => {
    const pickerId = React.useId();
    const [open, setOpen] = React.useState(false);
    // Internal state for the component (what user is selecting)
    const [internalRange, setInternalRange] = React.useState<{
      from?: Date;
      to?: Date;
    }>({});
    const [internalPreset, setInternalPreset] = React.useState<string | null>(
      null,
    );
    const [internalLabel, setInternalLabel] = React.useState<string | null>(
      null,
    );
    // The calendar is driven rather than left to its own devices, so a typed
    // date can move it to the month it lands in.
    const [visibleMonth, setVisibleMonth] = React.useState<Date>(() =>
      dayjs().subtract(1, "month").toDate(),
    );
    // Only the field being typed in holds its own text. Every other field reads
    // from internalRange, so there is one source of truth for the range and one
    // buffer for the half-typed date that is not a range yet.
    const [draft, setDraft] = React.useState<{
      end: RangeEnd;
      text: string;
    } | null>(null);

    // One instance each: the mask holds state about what is typed into the
    // field it is attached to.
    const fromOptions = React.useMemo(() => createTypeableDateMask(), []);
    const toOptions = React.useMemo(() => createTypeableDateMask(), []);
    const fromMask = useMaskito({ options: fromOptions });
    const toMask = useMaskito({ options: toOptions });

    // Initialize internal state when popover opens
    React.useEffect(() => {
      if (open) {
        if (value) {
          setInternalRange({ from: value.from, to: value.to });
          // Check if current value matches any preset
          const matchingPreset = presets.find((preset) => {
            const presetValue = preset.getValue();
            return (
              dayjs(presetValue.from).isSame(value.from, "day") &&
              dayjs(presetValue.to).isSame(value.to, "day")
            );
          });
          setInternalPreset(matchingPreset?.label || null);
          setInternalLabel(createDateLabel(value.from, value.to));
          setVisibleMonth(
            anchorMonth(RANGE_END.to, value.to, {
              from: value.from,
              to: value.to,
            }),
          );
        } else {
          setVisibleMonth(dayjs().subtract(1, "month").toDate());
        }
      }
    }, [open, value, presets]);

    // Apply changes on popover close (both Apply button and clicking outside)
    const handleOpenChange = (newOpen: boolean) => {
      if (!newOpen && open) {
        // Only apply if we have valid range and not already applied
        if (internalRange.from && internalRange.to) {
          handleApplyInternal();
        }
      }
      setOpen(newOpen);
      if (!newOpen) {
        // Reset internal state when closing
        setInternalRange({});
        setInternalPreset(null);
        setInternalLabel(null);
        setDraft(null);
      }
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      (onSelect || onChange)?.(null);
      setInternalRange({});
      setInternalPreset(null);
      setInternalLabel(null);
      setDraft(null);
    };

    const displayValue =
      open && internalLabel
        ? internalLabel
        : value
          ? createDateLabel(value.from, value.to)
          : placeholder;

    const handlePresetClick = (preset: TimeRangePreset) => {
      const range = preset.getValue();
      setInternalRange({ from: range.from, to: range.to });
      setInternalPreset(preset.label);
      setInternalLabel(createDateLabel(range.from, range.to));
      // Anchored the same way a typed end is, so the calendar always lands on
      // the range being described. Without this, picking "Last quarter" filled
      // the fields while the calendar sat on today, showing no selection at all.
      setVisibleMonth(anchorMonth(RANGE_END.to, range.to, range));
    };

    const handleCalendarSelect = (range?: { from?: Date; to?: Date }) => {
      if (!range?.from) return;

      // If only from is set (single day selection), use same date for both from and to
      // The actual start/end of day conversion will happen in the API call
      if (!range.to) {
        const singleDayRange = {
          from: range.from,
          to: range.from,
        };
        setInternalRange(singleDayRange);
        setInternalPreset(null);
        setInternalLabel(
          createDateLabel(singleDayRange.from, singleDayRange.to),
        );
        return;
      }

      setInternalRange(range);
      // Deselect any preset since this is a manual selection
      setInternalPreset(null);

      // Update internal label if we have both dates
      const finalFrom = dayjs(range.to).isBefore(range.from)
        ? range.to
        : range.from;
      const finalTo = dayjs(range.to).isBefore(range.from)
        ? range.from
        : range.to;

      setInternalLabel(createDateLabel(finalFrom, finalTo));
    };

    const textFor = (end: RangeEnd) => {
      if (draft?.end === end) return draft.text;
      const date = internalRange[end];
      return date ? dayjs(date).format(FORMAT_TYPEABLE_DATE) : "";
    };

    const handleTypedChange = (end: RangeEnd, text: string) => {
      setDraft({ end, text });

      const parsed = parseTypeableDate(text);
      if (!parsed) return;

      const next = { ...internalRange, [end]: parsed };
      setInternalRange(next);
      // Typing a date is a manual selection, the same as clicking one.
      setInternalPreset(null);
      setInternalLabel(
        next.from && next.to ? createDateLabel(next.from, next.to) : null,
      );
      setVisibleMonth(anchorMonth(end, parsed, next));
    };

    const handleApplyInternal = () => {
      if (internalRange.from && internalRange.to) {
        const finalFrom = dayjs(internalRange.to).isBefore(internalRange.from)
          ? internalRange.to
          : internalRange.from;
        const finalTo = dayjs(internalRange.to).isBefore(internalRange.from)
          ? internalRange.from
          : internalRange.to;

        const timeRange: TimeRange = {
          from: finalFrom,
          to: finalTo,
          label: createDateLabel(finalFrom, finalTo),
        };

        (onSelect || onChange)?.(timeRange);
      }
    };

    const handleApply = () => {
      handleApplyInternal();
      // Reset internal state before closing to prevent duplicate apply
      setInternalRange({});
      setInternalPreset(null);
      setInternalLabel(null);
      setDraft(null);
      setOpen(false);
    };

    return (
      <div className={cn({ "w-full": fullWidth })}>
        {label && (
          <label className="mb-1 block text-sm font-medium">
            {label}
            {required && <span className="ml-1">*</span>}
          </label>
        )}
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              ref={ref}
              className={cn(
                // Wide enough for a range that spans a year boundary, which is the
                // longest label this makes: "Dec 25, 2023 - Mar 31, 2024" needs 198px
                // and used to be cut off at 175px. A little slack on top of that.
                "flex h-10 w-[272px] items-center justify-between rounded-lg border border-input bg-card px-3 text-lg shadow-sm transition-colors",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                "cursor-pointer hover:border-ring",
                { "text-tertiary-text": !value && !open },
                { "ring-1 ring-ring": open },
                { "w-full": fullWidth },
              )}
              type="button"
            >
              <span className="truncate">{displayValue}</span>
              {value ? (
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Clear date range"
                  className="ml-2 flex h-6 w-6 min-w-6 cursor-pointer items-center justify-center rounded-full bg-surface-primary text-primary-text transition-colors hover:bg-green-20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:bg-surface-secondary"
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleClear(e as unknown as React.MouseEvent);
                    }
                  }}
                >
                  <XMarkIcon className="size-5" />
                </div>
              ) : (
                <CalendarDaysIcon
                  className={cn("ml-2 size-6 text-black", {
                    "text-interactive-text": open,
                  })}
                />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="flex">
              {/* Left sidebar with presets */}
              <div className="w-48 border-r">
                <div className="py-2">
                  {presets.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      className={cn(
                        "w-full px-4 py-3 text-left text-sm transition-colors",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                        "hover:bg-blue-5/50 active:bg-blue-5/70",
                        { "bg-blue-5": internalPreset === preset.label },
                      )}
                      onClick={() => handlePresetClick(preset)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handlePresetClick(preset);
                        }
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right side with calendar */}
              <div className="p-3">
                {allowTextEntry && (
                  <Stack
                    direction="row"
                    align="center"
                    gap="sm"
                    className="mb-3 border-b pb-3"
                  >
                    {[RANGE_END.from, RANGE_END.to].map((end) => (
                      <Stack key={end} direction="row" align="center" gap="xs">
                        <label
                          htmlFor={`${pickerId}-${end}`}
                          className="text-sm text-secondary-text"
                        >
                          {end === RANGE_END.from ? "From" : "To"}
                        </label>
                        <input
                          id={`${pickerId}-${end}`}
                          ref={end === RANGE_END.from ? fromMask : toMask}
                          type="text"
                          inputMode="numeric"
                          autoFocus={end === RANGE_END.from}
                          className="h-9 w-[130px] rounded-lg border border-input bg-card px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          placeholder={FORMAT_TYPEABLE_DATE_PLACEHOLDER}
                          value={textFor(end)}
                          onInput={(e) =>
                            handleTypedChange(end, e.currentTarget.value)
                          }
                          // Drop the buffer so the field falls back to the range
                          // it belongs to. A date that never parsed is simply
                          // not a date, and snapping back beats clearing the end
                          // someone already had.
                          onBlur={() => setDraft(null)}
                        />
                      </Stack>
                    ))}
                  </Stack>
                )}
                <Calendar
                  mode="range"
                  selected={internalRange as DateRange}
                  onSelect={handleCalendarSelect}
                  numberOfMonths={MONTHS_ON_SCREEN}
                  month={visibleMonth}
                  onMonthChange={setVisibleMonth}
                  autoFocus={!allowTextEntry}
                />
                <Stack direction="row" className="mt-3" justify="end">
                  <Button
                    size="sm"
                    variant="tertiary"
                    onClick={handleClear}
                    disabled={!internalRange.from && !internalRange.to}
                  >
                    Clear
                  </Button>
                  <Button size="sm" onClick={handleApply}>
                    Done
                  </Button>
                </Stack>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

TimeRangePicker.displayName = "TimeRangePicker";

export { TimeRangePicker, DEFAULT_PRESETS };
